import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { useHistory, useLocation } from 'react-router-dom';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useIntl } from '../../util/reactIntl';
import {
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
  NO_ACCESS_PAGE_VIEW_LISTINGS,
} from '../../util/urlHelpers';
import {
  isErrorNoViewingPermission,
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors';
import { hasPermissionToViewData, isUserAuthorized } from '../../util/userHelpers';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { manageDisableScrolling, isScrollingDisabled } from '../../ducks/ui.duck';
import { NamedRedirect } from '../../components';

import { SearchPageComponent } from './SearchPageWithGrid';
import { loadData as loadFlashSearchData } from './FlashSearchPage.duck';

const FLASH_LISTING_TYPE = 'flash_tattoo';

const EnhancedFlashSearchPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const searchListingsError = props.searchListingsError;
  if (isForbiddenError(searchListingsError)) {
    return (
      <NamedRedirect
        name="SignupPage"
        state={{ from: `${location.pathname}${location.search}${location.hash}` }}
      />
    );
  }

  const { currentUser, ...restOfProps } = props;
  const isPrivateMarketplace = config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasNoViewingRightsUser = currentUser && !hasPermissionToViewData(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(searchListingsError);
  const hasNoViewingRightsError = isErrorNoViewingPermission(searchListingsError);

  if ((isPrivateMarketplace && isUnauthorizedUser) || hasUserPendingApprovalError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  } else if (hasNoViewingRightsUser || hasNoViewingRightsError) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_VIEW_LISTINGS }}
      />
    );
  }

  // Build a flash-scoped config:
  // - Only show listing fields relevant to flash_tattoo (or unscoped)
  // - Deduplicate by key, last entry wins (code-side config overrides hosted asset)
  // - Remove dates/seats/price default filters (flash uses pub_flashPrice instead)
  // - Inject flashPrice range filter as a long-type field
  const rawFlashFields = (config.listing.listingFields || []).filter(field => {
    const { listingTypeConfig } = field;
    if (!listingTypeConfig?.limitToListingTypeIds) return true;
    return listingTypeConfig.listingTypeIds.includes(FLASH_LISTING_TYPE);
  });

  // Merge by key: later entries (code-side) extend earlier ones (hosted asset),
  // but preserve enumOptions from the hosted asset when our code-side entry omits them.
  const fieldsByKey = new Map();
  rawFlashFields.forEach(field => {
    if (fieldsByKey.has(field.key)) {
      const existing = fieldsByKey.get(field.key);
      fieldsByKey.set(field.key, {
        ...existing,
        ...field,
        enumOptions: field.enumOptions?.length ? field.enumOptions : existing.enumOptions,
      });
    } else {
      fieldsByKey.set(field.key, field);
    }
  });
  const deduplicatedFields = [...fieldsByKey.values()];

  const flashPriceFieldConfig = {
    key: 'flashPrice',
    scope: 'public',
    schemaType: 'long',
    minimum: 1,
    maximum: 5000,
    step: 10,
    listingTypeConfig: { limitToListingTypeIds: true, listingTypeIds: [FLASH_LISTING_TYPE] },
    filterConfig: {
      indexForSearch: true,
      label: 'Preço (R$)',
      group: 'primary',
    },
  };

  // Injected directly because code-side configListing.js fields are never merged
  // at runtime (mergeDefaultTypesAndFieldsForDebugging is false in production).
  const flashSizeCategoryFieldConfig = {
    key: 'flashSizeCategory',
    scope: 'public',
    schemaType: 'enum',
    enumOptions: [
      { option: 'key_size', label: 'Chave 🔑' },
      { option: 'credit_card_size', label: 'Cartão de crédito 🪪' },
      { option: 'wrist_size', label: 'Punho ✊' },
      { option: 'hand_size', label: 'Mão 🖐️' },
      { option: 'half_sleeve_or_larger', label: 'Meia manga ou maior 💪' },
    ],
    listingTypeConfig: { limitToListingTypeIds: true, listingTypeIds: [FLASH_LISTING_TYPE] },
    filterConfig: {
      indexForSearch: true,
      filterType: 'SelectSingleFilter',
      label: 'Tamanho',
      group: 'primary',
    },
    showConfig: { label: 'Tamanho', isDetail: true },
    saveConfig: { label: 'Tamanho da tattoo', isRequired: false },
  };

  const flashConfig = {
    ...config,
    listing: {
      ...config.listing,
      listingFields: [...deduplicatedFields, flashSizeCategoryFieldConfig, flashPriceFieldConfig],
    },
    search: {
      ...config.search,
      defaultFilters: (config.search.defaultFilters || []).filter(
        f => !['dates', 'seats', 'price'].includes(f.key)
      ),
    },
  };

  return (
    <SearchPageComponent
      config={flashConfig}
      routeConfiguration={routeConfiguration}
      routeName="FlashSearchPage"
      hideFilterSidebar={true}
      intl={intl}
      history={history}
      location={location}
      currentUser={currentUser}
      {...restOfProps}
    />
  );
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    currentPageResultIds,
    pagination,
    searchInProgress,
    searchListingsError,
    searchParams,
  } = state.SearchPage;
  const listings = getListingsById(state, currentPageResultIds);

  return {
    currentUser,
    listings,
    pagination,
    scrollingDisabled: isScrollingDisabled(state),
    searchInProgress,
    searchListingsError,
    searchParams,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  onReloadFavorites: (search, config) => dispatch(loadFlashSearchData({}, search, config)),
});

const FlashSearchPage = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(EnhancedFlashSearchPage);

export default FlashSearchPage;
